<template>
  <div
    id="cancel-modal"
    aria-hidden="true"
    class="modal fade"
    data-backdrop="exampleModalLabel"
    data-keyboard="false"
    tabindex="-1"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg"
      :class="{ 'modal-dialog-centered': isMobile }"
      style="max-width: 514px !important; max-height: 282px !important"
    >
      <div class="modal-content" style="border-radius: 18px">
        <div
          class="modal-body p-4"
          style="
            border: 1px solid #93751f;
            border-radius: 18px;
            box-shadow: 0px 3px 14px #ffffff60;
            border: 1px solid #93751f;
          "
        >
          <div v-if="$store.state.CancelReservation" class="p-2">
            <h1
              class="font-weight-bold font-20 text-left cancel-session-header"
              style="color: #ffc627"
            >
              Cancel reservation
            </h1>
            <div class="cancel-reservation-mobile" style="font-weight: 400">
              <p class="" style="font-size: 14px; font-weight: bold">
                <!-- Would you like the cancel the reservations? -->
                You are about to cancel this reservation for:
                <span class="d-block" style="font-size: 14px; font-weight: normal">
                  '{{ $store.state.CancelReservation.first_name }}
                  {{ $store.state.CancelReservation.last_name }}'
                </span>
                <!-- A session you want to cancel already has a user registered. Do you
                still want to cancel this session and all current reservations? -->
              </p>
            </div>
            <div class="d-flex justify-content-center">
              <button class="btn cancel" data-bs-dismiss="modal">No</button>
              <button class="btn accept" @click="CancelReservation($store.state.CancelReservation)">
                Yes, cancel
                <span v-if="api">...</span>
              </button>
            </div>
          </div>
          <div v-else class="p-2">
            <h1
              class="font-weight-bold font-27 text-left cancel-session-header"
              style="color: #ffc627"
            >
              Cancel session
            </h1>
            <div class="p-5 my-2 font-24 cancel-session-mobile" style="font-weight: 400">
              <!-- Cancelling this session will remove all registered users. Are you
              sure you would like to continue? -->
              <p class="d-none">Are you sure you would like to cancel this reservation?</p>
              <p class="d-flex">Are you sure you want to cancel this session?</p>
              <span>
                <span v-if="$store.state.cancelSession && $store.state.cancelSession.title_name">
                  {{ $store.state.cancelSession.title_name }}
                </span>
                <span class="d-none">,</span>
                <span class="d-inline">|</span>
                <span
                  v-if="$store.state.cancelSession && $store.state.cancelSession.start_timestamp"
                >
                  {{ getTrimTime($store.state.cancelSession.start_timestamp) }}
                </span>
                |
                <span
                  v-if="$store.state.cancelSession && $store.state.cancelSession.start_timestamp"
                >
                  {{ getTrimDate($store.state.cancelSession.start_timestamp) }}
                </span>
              </span>
            </div>
            <div class="d-flex justify-content-center">
              <button class="btn cancel" data-bs-dismiss="modal">No</button>
              <button class="btn accept" @click="CancelSession($store.state.cancelSession)">
                Yes, cancel
                <span v-if="api">...</span>
              </button>
            </div>
            <p v-if="api">Wait for a while, it may take few seconds.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation that the cancellation actually went through. Shown only after the
         DELETE resolves 2xx, so it can never report success for a failed cancel.
         SuccessModal teleports itself to <body>, so nesting it here is only for
         ownership — it does not render inside the hidden modal above. -->
    <SuccessModal
      v-model="showCancelSuccess"
      message="Show successfully cancelled"
      button-text="Ok"
      :icon-src="successIcon"
    />
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import { API } from '@/utils/amplifyCompat';
import moment from 'moment';
import SuccessModal from '@/components/scheduler/modals/SuccessModal.vue';
import { parseApiErrorMessage } from '@/utils/apiError';

export default {
  components: { SuccessModal },
  data() {
    return {
      api: false,
      showCancelSuccess: false,
      successIcon: require('@/assets/icons/check-solid.svg'),
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
  },
  methods: {
    getTrimDate(timestamp) {
      const temp = timestamp.split('T');
      return moment(temp[0]).format('ddd, MMM D, YYYY');
    },
    getTrimTime(timestamp) {
      return moment(timestamp).format('hh:mm A');
    },
    async CancelSession() {
      const session = this.$store.state.cancelSession;
      if (!session) return;

      const siteId = this.$store.state.selectedSiteForScheduler.site_id;
      this.api = true;

      try {
        // A show with student reservations cannot be cancelled (DSL-708). Re-check
        // against the server rather than trusting what the card loaded earlier, since a
        // student may have reserved since then.
        const orders =
          (await API.get('asulink', `/scheduler/session/${session.session_id}/orders`)) || [];
        if (orders.length > 0) {
          this.showError(
            `This show has ${orders.length} reservation${orders.length === 1 ? '' : 's'}. ` +
              'Cancel all reservations before cancelling the show.'
          );
          return;
        }

        // Cancel through the middleware, never straight at the DSL API. A direct
        // `asulink` DELETE removes the session downstream but leaves the middleware
        // still holding the slot, so the other portal keeps showing that time as
        // occupied and it can never be rebooked there. See DSL-667.
        // API.del throws on any non-2xx, so reaching this line means the cancel
        // succeeded. Close the confirmation dialog, then report it.
        await API.del('proxy', `/${siteId}/dsl/scheduler/session/${session.session_id}`);

        const modalEl = document.getElementById('cancel-modal');
        if (modalEl) {
          // Wait for Bootstrap to finish hiding before showing the confirmation, so it
          // removes its own backdrop and the two dialogs never overlap.
          modalEl.addEventListener(
            'hidden.bs.modal',
            () => {
              this.showCancelSuccess = true;
            },
            { once: true }
          );
          Modal.getOrCreateInstance(modalEl).hide();
        } else {
          this.showCancelSuccess = true;
        }

        this.$store.state.viewFromMenu = '';
        this.$nextTick(() => {
          this.$store.state.viewFromMenu = 'Pod Schedule';
        });

        this.$store.state.loader = true;
        this.$store.dispatch('setSchedulerSessions', {
          selected_site_id: this.$store.state.selected_site_id,
          podSessionDate: this.$store.state.podSessionDate,
        });
      } catch (err) {
        // API.del throws a plain Error, so err.response is undefined — reading
        // err.response.data here used to throw inside the catch and show nothing at all.
        // parseApiErrorMessage pulls the server's own message out of the thrown
        // "HTTP {status}: {statusText} - {body}" string.
        this.showError(parseApiErrorMessage(err, 'Could not cancel the show. Please try again.'));
      } finally {
        this.api = false;
      }
    },
    // Close the confirmation first and wait for Bootstrap to finish hiding it, so the
    // error dialog never stacks on top of it with two backdrops.
    showError(message) {
      this.$store.state.error = { message };
      const openError = () => {
        const errorModalEl = document.getElementById('error-checkin');
        if (errorModalEl) {
          Modal.getOrCreateInstance(errorModalEl).show();
        }
      };
      const modalEl = document.getElementById('cancel-modal');
      if (modalEl && modalEl.classList.contains('show')) {
        modalEl.addEventListener('hidden.bs.modal', openError, { once: true });
        Modal.getOrCreateInstance(modalEl).hide();
      } else {
        openError();
      }
    },
    CancelReservation(data) {
      this.api = true;
      API.del(
        'proxy',
        `/${this.$store.state.selectedSiteForScheduler.site_id}/dsl/scheduler/session/${data.session_id}/order/${data.ticket_id}`
      )
        .then(() => {
          const modalEl = document.getElementById('cancel-modal');
          if (modalEl) {
            const modal = Modal.getOrCreateInstance(modalEl);
            modal.hide();
          }

          this.$store.state.viewFromMenu = '';
          this.api = false;
          this.$nextTick(() => {
            this.$store.state.viewFromMenu = 'Pod Schedule';
          });

          const myData = {
            selected_site_id: this.$store.state.selected_site_id,
            podSessionDate: this.$store.state.podSessionDate,
          };
          this.$store.dispatch('setSchedulerSessions', myData);
        })
        .catch((err) => {
          window.console.log('Error occured', err);
          this.api = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@media only screen and (max-width: 767px) {
  .btn {
    font-size: 14px !important;
    border-radius: 8px !important;
    background-color: #151515 !important;
    cursor: pointer !important;
    font-weight: bold !important;
    margin: 0.5rem !important;
    padding: 0rem 1rem !important;
    width: 108px !important;
    height: 35px !important;
  }
  .accept {
    border: 0.5px solid #ffc627 !important;
    color: #151515 !important;
    background-color: #ffc627 !important;
  }
  .cancel {
    border: 0.5px solid #ffffff !important;
    color: #ffffff !important;
  }
  .cancel-session-mobile {
    padding: 1rem 0rem !important;
    display: flex !important;
    align-items: center !important;
    flex-direction: column !important;
  }
  .cancel-session-mobile p {
    margin: 0 !important;
    font-weight: medium !important;
    font-size: 14px !important;
    color: #ffffff !important;
  }
  .cancel-session-mobile span {
    font-weight: medium !important;
    font-size: 12px !important;
    color: #ffffff !important;
  }
  .cancel-reservation-mobile {
    padding: 1rem 0rem !important;
    display: flex !important;
    align-items: center !important;
    flex-direction: column !important;
  }
  .cancel-reservation-mobile p {
    margin: 0 !important;
    font-weight: medium !important;
    font-size: 14px !important;
    color: #ffffff !important;
  }
  .cancel-reservation-mobile span {
    font-weight: normal !important;
    font-size: 14px !important;
    color: #ffffff !important;
  }
  .cancel-session-header {
    font-family: 'kallisto-bold' !important;
    font-size: 16px !important;
  }
}
.btn {
  font-size: 16px;
  border-radius: 8px !important;
  background-color: #151515 !important;
  cursor: pointer !important;
  font-weight: bold !important;
  margin: 0.5rem !important;
  padding: 0rem 1rem !important;
  width: 136px;
  height: 42px;
}
.accept {
  border: 0.5px solid #ffc627 !important;
  color: #151515 !important;
  background-color: #ffc627 !important;
}
.cancel {
  border: 0.5px solid #ffffff !important;
  color: #ffffff !important;
}
.cancel-session-mobile {
  padding: 1rem 0rem !important;
  display: flex !important;
  align-items: center !important;
  flex-direction: column !important;
}
.cancel-session-mobile p {
  margin: 0 !important;
  font-weight: medium !important;
  font-size: 22px;
  color: #ffffff !important;
}
.cancel-session-mobile span {
  font-weight: medium !important;
  font-size: 18px;
  color: #ffffff !important;
}
.cancel-reservation-mobile {
  padding: 1rem 0rem !important;
  display: flex !important;
  align-items: center !important;
  flex-direction: column !important;
}
.cancel-reservation-mobile p {
  margin: 0 !important;
  font-weight: normal !important;
  font-size: 22px;
  color: #ffffff !important;
}
.cancel-reservation-mobile span {
  font-weight: normal !important;
  font-size: 18px;
  color: #ffffff !important;
}
.cancel-session-header {
  font-family: 'kallisto-bold' !important;
  font-size: 20px;
}
.cancel:hover {
  color: #ffffff !important;
}
</style>
